
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { getCurrentCameraPreview } from '@/components/CameraPreview.vue';
import FaceDirectionTracker, { TrackerErrorEventArgs } from '@/components/facetracker/FaceDirectionTracker.vue';
import FaceDirectionArrow from '@/components/facetracker/FaceDirectionArrow.vue';
import FaceDirectionGuide, { StatusRingGauge } from '@/components/facetracker/FaceDirectionGuide.vue';
import FaceDirectionResultEffect, { StatusResultEffect } from '@/components/facetracker/FaceDirectionResultEffect.vue';
import FaceDirectionMessage, { MessageType } from '@/components/facetracker/FaceDirectionMessage.vue';
import StepProgress from '@/components/facetracker/StepProgress.vue';
import { MessageTypeErrorFaceAction } from '@/components/ModalDefaultPopupBodyErrorFaceAction.vue';
import { Direction, DirectionDetectionPhase, TrackerStatus, TrackerType, ErrorType } from '@/services/facetracker';
import { setTimeoutAsync } from '@/services/utils';
import { CheckDirectionStatus } from '@/api/facetracker/FaceTracker';
import { CameraChildErrorCode } from '@/resources/error/CameraChildErrorCode';

// Minimum API calling interval in milliseconds
const MINIMUM_INTERVAL = 200; // 5fps

@Component({
  components: {
    FaceDirectionTracker,
    FaceDirectionArrow,
    FaceDirectionGuide,
    FaceDirectionResultEffect,
    FaceDirectionMessage,
    StepProgress
  }
})
export default class FaceDirection extends Vue {
  @Prop({ default: TrackerType.Server })
  trackerType!: TrackerType;

  $refs!: {
    tracker: FaceDirectionTracker;
  };

  messageType: MessageType = MessageType.None;
  lookLevel: number = 0;
  isLoading: boolean = !this.$store.getters['camera/isFaceActionSkipLoading'];
  isStartButtonEnabled: boolean = false;
  showLiveArrow: boolean = false;
  guideStatusResultEffect: StatusResultEffect = 'none';
  guideStatusRingGauge: StatusRingGauge = 'none';
  guideProgress: number = 0;

  get isMessageVisible(): boolean {
    return this.messageType !== MessageType.None;
  }

  /** update 前に待機する場合の開始基準となる時間 */
  private delayStartTime: number = 0;

  /** update 前に待機が必要な時間 (ms) */
  private delayForUpdate: number = 0;

  private tracker!: FaceDirectionTracker;

  private directionTargets: Direction[] = [];

  private directionProgress = DirectionDetectionPhase.None;
  private frameRequest = 0;
  private isInError = false;

  public async created(): Promise<void> {
    // ページマウント時の設定
    await this.$store.dispatch('camera/mountedSetting', this.$route.path);

    // 条件によってはローディングを表示しないようにする (別ページのエラーに飛んで戻ってきた時)
    if (this.$store.getters['camera/isFaceActionSkipLoading']) {
      this.$store.commit('camera/setFaceActionSkipLoading', false);
      this.isLoading = false;
      this.isStartButtonEnabled = false;
      this.messageType = MessageType.Starting;
    }
  }

  mounted() {
    this.tracker = this.$refs.tracker;
  }

  beforeDestroy() {
    cancelAnimationFrame(this.frameRequest);
    this.frameRequest = -1;
  }

  async update() {
    await Promise.all([setTimeoutAsync(MINIMUM_INTERVAL), this.tracker.update()]);
    if (this.tracker.currentStatus === TrackerStatus.InError || this.tracker.currentStatus === TrackerStatus.Ended) {
      return;
    }

    if (this.delayForUpdate > 0) {
      if (this.delayStartTime > 0) this.delayForUpdate = this.delayForUpdate - (Date.now() - this.delayStartTime);
      if (this.delayForUpdate > 0) await setTimeoutAsync(this.delayForUpdate);
      this.delayStartTime = 0;
      this.delayForUpdate = 0; // reset
    }

    if (this.frameRequest < 0) {
      return;
    }

    this.frameRequest = requestAnimationFrame(this.update);
  }

  private async onTrackerInitialized() {
    this.isLoading = false;
    this.isStartButtonEnabled = true;
    this.messageType = MessageType.Starting;
    this.tracker.reset();
  }

  private onStart() {
    this.update(); // start update loop
  }

  private onCalibrate() {
    this.tracker.calibrate();
  }

  private onTrackerError(e: TrackerErrorEventArgs) {
    let fields: string[] | undefined = undefined;
    let faceActionError: string | undefined = undefined;
    if (e.isManyRetry) {
      fields = [CameraChildErrorCode.Automatic, CameraChildErrorCode.ManyRetry];
    } else {
      const messageTypeErrorFaceAction: MessageTypeErrorFaceAction = {
        [ErrorType.Unknown]: MessageTypeErrorFaceAction.Unknown,
        [ErrorType.FaceNotFound]: MessageTypeErrorFaceAction.FaceNotFound,
        [ErrorType.Timeout]: MessageTypeErrorFaceAction.Timeout,
        [ErrorType.ServerError]: MessageTypeErrorFaceAction.Unknown,
        [ErrorType.Network]: MessageTypeErrorFaceAction.Unknown,
        [ErrorType.Loading]: MessageTypeErrorFaceAction.Unknown,
        [ErrorType.DifferentDirection]: MessageTypeErrorFaceAction.DifferentDirection,
        [ErrorType.InvalidScreenOrientation]: MessageTypeErrorFaceAction.InvalidScreenOrientation,
        [ErrorType.CVInvalidFeatureBrightness]: MessageTypeErrorFaceAction.CVInvalidFeatureBrightness,
        [ErrorType.CVInvalidFeatureSharpness]: MessageTypeErrorFaceAction.CVInvalidFeatureSharpness,
        [ErrorType.CVInvalidFeatureHighlight]: MessageTypeErrorFaceAction.CVInvalidFeatureHighlight,
        [ErrorType.CVInvalidFeatureSunglass]: MessageTypeErrorFaceAction.CVInvalidFeatureSunglass,
        [ErrorType.CVInvalidFeatureStraight]: MessageTypeErrorFaceAction.CVInvalidFeatureStraight,
        [ErrorType.CVInvalidFeatureIsGrayscale]: MessageTypeErrorFaceAction.CVInvalidFeatureIsGrayscale
      }[e.errorType];

      faceActionError = `${messageTypeErrorFaceAction}`;
    }

    // エラーページに遷移する
    const query: { [key: string]: string | string[] | undefined } = { fields, face_action_error: faceActionError };
    this.$router.push(Object.assign({}, this.$store.getters['camera/locationError'], { query }));
  }

  private async onStatusChanged(status: TrackerStatus) {
    switch (status) {
      case TrackerStatus.EstablishingSession:
        await this.onTrackerInitialized();
        return;

      case TrackerStatus.Calibrating:
        this.messageType = MessageType.Calibrate;
        this.guideStatusRingGauge = 'none';
        this.guideProgress = 0;
        this.directionProgress = DirectionDetectionPhase.None;
        return;

      case TrackerStatus.TakingSelfie:
        // 自撮り撮影開始時間の記録
        this.delayStartTime = Date.now();
        return;

      case TrackerStatus.Detecting:
        this.messageType = MessageType.Wait;
        this.guideStatusRingGauge = 'calibrated';
        this.guideProgress = 0;
        this.delayForUpdate = 500;
        return;

      case TrackerStatus.InError:
        return;

      case TrackerStatus.Ended:
        // Android11でページ遷移のタイミングで固まってしまう不具合があり
        // おそらくなんらかのリソースが切迫しているので、とりあえずカメラを止めてみる
        getCurrentCameraPreview()?.pauseVideo();
        this.$router.push(this.$store.getters['camera/locationNext']);
        return;
      default:
        break;
    }
  }

  private async onDirectionPhaseChanged(phase: DirectionDetectionPhase) {
    if (this.tracker.currentStatus !== TrackerStatus.Detecting) {
      return;
    }
    this.directionProgress = phase;

    switch (phase) {
      case DirectionDetectionPhase.Phase1:
      case DirectionDetectionPhase.Phase2:
      case DirectionDetectionPhase.Phase3:
        switch (this.currentDirectionTarget) {
          case Direction.Up:
            this.messageType = MessageType.DirectionUp;
            break;
          case Direction.Down:
            this.messageType = MessageType.DirectionDown;
            break;
          case Direction.Left:
            this.messageType = MessageType.DirectionLeft;
            break;
          case Direction.Right:
            this.messageType = MessageType.DirectionRight;
            break;
        }
        this.guideStatusRingGauge = 'calibrated';
        this.delayForUpdate = 2000;
        return;

      case DirectionDetectionPhase.Center1:
      case DirectionDetectionPhase.Center2:
        this.messageType = MessageType.DirectionCenter;
        this.guideStatusRingGauge = 'success';
        this.delayForUpdate = 2000;
        return;

      case DirectionDetectionPhase.Completed:
        this.messageType = MessageType.Complete;
        this.guideStatusRingGauge = 'success';
        return;

      default:
        break;
    }
  }

  private async onDirectionStatusChanged(status: CheckDirectionStatus) {
    switch (status) {
      case CheckDirectionStatus.Success:
        // 「真ん中を向いてください」の際には成功表示を出さない
        if (
          this.directionProgress === DirectionDetectionPhase.Center1 ||
          this.directionProgress === DirectionDetectionPhase.Center2
        ) {
          return;
        }
        this.success();
        this.delayForUpdate = 2000;
        return;

      case CheckDirectionStatus.DifferentDirection:
      case CheckDirectionStatus.FaceNotFound:
        this.failure(status === CheckDirectionStatus.FaceNotFound);
        this.delayForUpdate = 2000;
        return;

      default:
        return;
    }
  }

  private onDirectionsChanged(value: Direction[]) {
    this.directionTargets = value;
  }

  private onCalibrateProgress(currentCount: number, maxCount: number) {
    this.guideProgress = currentCount / maxCount;
  }

  private get currentDirectionTarget(): Direction {
    return this.directionTargets[this.directionProgress % this.directionTargets.length];
  }

  private success() {
    this.guideStatusResultEffect = 'success';
  }

  private failure(isNoFace: boolean) {
    this.guideStatusResultEffect = 'failure';
    this.guideStatusRingGauge = 'error';
    this.guideProgress = 0;

    this.messageType = MessageType.None;
  }

  @Watch('messageType')
  private onMessageChange(messageType: MessageType) {
    switch (messageType) {
      case MessageType.DirectionUp:
        this.guideStatusResultEffect = 'up';
        break;
      case MessageType.DirectionDown:
        this.guideStatusResultEffect = 'down';
        break;
      case MessageType.DirectionLeft:
        this.guideStatusResultEffect = 'left';
        break;
      case MessageType.DirectionRight:
        this.guideStatusResultEffect = 'right';
        break;
    }
  }
}
